"use client";
import { useCallback, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { TextSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"

// --- Utils ---
import { getAnchorNodeAndPos } from "@/tiptap/lib/tiptap-advanced-utils"

// --- Icons ---
import { AlignTopIcon } from "@/tiptap/icons/align-top-icon"
import { AlignBottomIcon } from "@/tiptap/icons/align-bottom-icon"

export const MOVE_UP_SHORTCUT_KEY = "mod+shift+ArrowUp"
export const MOVE_DOWN_SHORTCUT_KEY = "mod+shift+ArrowDown"

/**
 * Checks if a node can be moved in the specified direction
 */
export function canMoveNode(editor, direction) {
  if (!editor || !editor.isEditable) return false
  const nodeInfo = getAnchorNodeAndPos(editor)
  if (!nodeInfo) return false

  try {
    const { pos } = nodeInfo
    const $pos = editor.state.doc.resolve(pos)
    const parent = $pos.parent
    const index = $pos.index()

    return direction === "up" ? index > 0 : index < parent.childCount - 1
  } catch {
    return false
  }
}

/**
 * Moves a node in the editor
 */
export function moveNode(editor, direction) {
  if (!editor || !editor.isEditable) return false
  const nodeInfo = getAnchorNodeAndPos(editor)
  if (!nodeInfo) return false

  try {
    const { pos, node } = nodeInfo
    const tr = editor.state.tr
    const $pos = tr.doc.resolve(pos)
    const parent = $pos.parent
    const index = $pos.index()

    if (index < 0 || index >= parent.childCount) {
      return false
    }

    if (direction === "up" && index > 0) {
      const prevNode = parent.child(index - 1)
      const prevSize = prevNode.nodeSize

      const movedNode = node.type.create(node.attrs, node.content, node.marks)
      tr.deleteRange(pos, pos + node.nodeSize)
      const insertPos = pos - prevSize
      tr.insert(insertPos, movedNode)
      tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos)))
    } else if (direction === "down" && index < parent.childCount - 1) {
      const nextNode = parent.child(index + 1)
      const nextSize = nextNode.nodeSize

      const movedNode = node.type.create(node.attrs, node.content, node.marks)
      tr.deleteRange(pos, pos + node.nodeSize)
      const insertPos = pos + nextSize
      tr.insert(insertPos, movedNode)
      tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos)))
    } else {
      return false
    }

    editor.view.dispatch(tr)
    return true
  } catch (err) {
    console.error("Error moving node:", err)
    return false
  }
}

/**
 * Determines if the move button should be shown
 */
export function shouldShowButton(props) {
  const { editor, direction, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  const hasNode = !!getAnchorNodeAndPos(editor)
  const movable = canMoveNode(editor, direction)

  return hasNode && movable
}

/**
 * Custom hook that provides move node functionality for Tiptap editor
 */
export function useMoveNode(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    direction,
    onMoved,
  } = config
  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()

  const [isVisible, setIsVisible] = useState(true)
  const [canMoveNodeState, setCanMoveNodeState] = useState(false)

  const handleMoveNode = useCallback(() => {
    if (!canMoveNodeState) return false

    const success = moveNode(editor, direction)
    if (success) onMoved?.(direction)

    return success
  }, [editor, direction, onMoved, canMoveNodeState])

  useEffect(() => {
    if (!editor) return

    const update = () => {
      setIsVisible(shouldShowButton({ editor, direction, hideWhenUnavailable }))
      setCanMoveNodeState(canMoveNode(editor, direction))
    }

    update()

    editor.on("selectionUpdate", update)
    return () => {
      editor.off("selectionUpdate", update)
    };
  }, [editor, direction, hideWhenUnavailable])

  const shortcutKeys =
    direction === "up" ? MOVE_UP_SHORTCUT_KEY : MOVE_DOWN_SHORTCUT_KEY

  useHotkeys(shortcutKeys, (event) => {
    event.preventDefault()
    handleMoveNode()
  }, {
    enabled: isVisible && canMoveNodeState,
    enableOnContentEditable: !isMobile,
    enableOnFormTags: true,
  }, [handleMoveNode, isVisible, canMoveNodeState, isMobile])

  const label = direction === "up" ? "Move Up" : "Move Down"
  const Icon = direction === "up" ? AlignTopIcon : AlignBottomIcon

  return {
    isVisible,
    handleMoveNode,
    canMoveNode: canMoveNodeState,
    label,
    shortcutKeys,
    Icon,
  }
}
